import Swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'
import { getErrorMessage } from '@/utils/apiError'

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 2500,
  timerProgressBar: true,
  // Pausa al pasar el mouse para poder terminar de leer mensajes largos.
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer)
    toast.addEventListener('mouseleave', Swal.resumeTimer)
  }
})

const ERROR_TIMER = 6000

/**
 * Toast de error con el mensaje real del backend (ver getErrorMessage).
 * `fallback` describe la acción que falló y solo se usa si el backend no dice nada útil.
 * También acepta un mensaje ya resuelto en lugar del error.
 */
const toastError = (error, fallback) => Toast.fire({
  icon: 'error',
  title: typeof error === 'string' ? error : getErrorMessage(error, fallback),
  timer: ERROR_TIMER
})

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')

const MAX_LISTED_FAILURES = 3

/**
 * Un solo aviso para una operación en lote. Con dos toasts seguidos (éxitos y
 * fallos) SweetAlert solo muestra el último, y el mensaje de fallo no decía
 * cuál factura falló ni por qué.
 *
 * @param {PromiseSettledResult[]} results  Resultado de Promise.allSettled, en el mismo orden que `items`.
 * @param {Array} items                     Elementos procesados.
 * @param {(item) => string} getLabel       Cómo identificar cada elemento en la lista de fallos.
 * @param {(count) => string} successText   Texto para los que salieron bien.
 * @param {(count) => string} failureText   Texto para los que fallaron.
 * @param {string} fallback                 Motivo si el backend no devolvió mensaje.
 */
const toastBulkResult = ({ results, items, getLabel, successText, failureText, fallback }) => {
  const failures = results
    .map((result, index) => ({ result, item: items[index] }))
    .filter(({ result }) => result.status === 'rejected')
  const succeeded = results.length - failures.length

  if (failures.length === 0) {
    return Toast.fire({ icon: 'success', title: successText(succeeded) })
  }

  const listed = failures.slice(0, MAX_LISTED_FAILURES).map(({ result, item }) =>
    `<li><strong>${escapeHtml(getLabel(item))}</strong>: ${escapeHtml(getErrorMessage(result.reason, fallback))}</li>`
  )
  const hidden = failures.length - listed.length
  if (hidden > 0) listed.push(`<li>y ${hidden} más</li>`)

  return Toast.fire({
    icon: succeeded > 0 ? 'warning' : 'error',
    title: succeeded > 0
      ? `${successText(succeeded)} · ${failureText(failures.length)}`
      : failureText(failures.length),
    html: `<ul style="margin:4px 0 0; padding-left:18px; text-align:left; font-size:13px;">${listed.join('')}</ul>`,
    timer: ERROR_TIMER + 4000
  })
}

export default {
  install(app) {
    app.config.globalProperties.$toast = Toast
  }
}

export { Toast, toastError, toastBulkResult }
